import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import groupService from '../../api/groupService';
import { useGroup } from '../../context/GroupContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faCalendarAlt,
  faDollarSign,
  faTicketAlt,
  faHandHoldingHeart,
  faCheckCircle,
  faHourglassHalf,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from '../../components/AdminLayout';

// Loading skeleton component
const LoadingSkeleton = ({ rows = 3 }) => (
  <div className="animate-pulse space-y-3">
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="flex space-x-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded flex-1"></div>
      </div>
    ))}
  </div>
);

// Summary card skeleton
const SummaryCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-6 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="h-3 bg-gray-200 rounded w-24 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
    </div>
  </div>
);

const MemberProfilePage = () => {
  const { id: groupId, userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminGroup } = useGroup();
  
  // Get member data passed from navigation (if available)
  const passedMemberData = location.state?.memberData;
  
  const [memberData, setMemberData] = useState(passedMemberData ? {
    profile: {
      id: passedMemberData.user_id || passedMemberData.id,
      name: passedMemberData.name,
      email: passedMemberData.email,
      phone: passedMemberData.phone,
      created_at: passedMemberData.joined_at || passedMemberData.created_at,
      avatar_url: passedMemberData.avatar_url || passedMemberData.profile_image
    },
    rsvps: [],
    ticketPurchases: [],
    donations: [],
    pledges: [],
    summary: {
      totalDonations: 0,
      totalTicketPurchases: 0,
      totalPledges: 0,
      unpaidPledges: 0
    }
  } : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    rsvps: true,
    tickets: true,
    donations: true,
    pledges: true
  });

  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!adminGroup?.id || !userId) return;
      
      // If we already have basic member data, fetch additional details
      if (passedMemberData) {
        console.log('✅ Using passed member data, fetching additional details...');
        console.log('🔍 Passed member data:', passedMemberData);
        console.log('🔍 userId from URL params:', userId);
        console.log('🔍 Member user_id:', passedMemberData.user_id);
        console.log('🔍 Member id:', passedMemberData.id);
        
        try {
          // Fetch donations, pledges, RSVPs, and ticket purchases in parallel
          const [donationsRes, pledgesRes, eventsRes] = await Promise.all([
            groupService.getGroupDonations(adminGroup.id, {}).catch(err => {
              console.error('Error fetching donations:', err);
              return { success: false, data: [] };
            }),
            groupService.getPledges(adminGroup.id).catch(err => {
              console.error('Error fetching pledges:', err);
              return { success: false, data: [] };
            }),
            groupService.getGroupEvents(adminGroup.id).catch(err => {
              console.error('Error fetching events:', err);
              return { success: false, data: [] };
            })
          ]);

          console.log('📊 Donations response:', donationsRes);
          console.log('📊 Pledges response:', pledgesRes);
          
          // We have auth_user_id from URL (userId = 431319a8...)
          // We need native_users.id for donations/pledges (a904b8a5...)
          // Strategy: Look through ALL donations and pledges to find any with matching auth_user_id in metadata or email
          // Then extract the native_users.id or member_id from that record
          
          let nativeUserId = null;
          
          // Try to find native_users.id from donations first
          if (donationsRes.success && donationsRes.data) {
            // Check if any donation has metadata with matching original_user_id (which might be auth_user_id)
            const metadataMatch = donationsRes.data.find(d => 
              d.metadata?.original_user_id === userId
            );
            
            if (metadataMatch) {
              nativeUserId = metadataMatch.user_id;
              console.log('✅ Found native_users.id from donation metadata:', nativeUserId);
            } else if (passedMemberData.email) {
              // Fallback: match by email (not ideal but works if emails are unique)
              const emailMatch = donationsRes.data.find(d => 
                d.native_users?.email === passedMemberData.email
              );
              if (emailMatch) {
                nativeUserId = emailMatch.user_id;
                console.log('⚠️ Found native_users.id by email match (fallback):', nativeUserId);
              }
            }
          }
          
          // If still not found, try pledges
          if (!nativeUserId && pledgesRes.data) {
            // Check if any pledge has the userId as member_id (in case pledges use auth_user_id)
            const authIdMatch = pledgesRes.data.find(p => p.member_id === userId);
            if (authIdMatch) {
              // If pledges use auth_user_id, we need to find the corresponding native_users.id
              // This won't help us, skip this approach
            }
          }
          
          console.log('🔑 Using native_users.id:', nativeUserId, 'for filtering');
          
          // Filter donations by user_id (which is native_users.id)
          const userDonations = donationsRes.success && nativeUserId
            ? (donationsRes.data || []).filter(d => {
                const matches = d.user_id === nativeUserId;
                if (matches) {
                  console.log('✅ Found matching donation:', d.id);
                }
                return matches;
              })
            : [];

          console.log('✅ User donations found:', userDonations.length);
          console.log('📊 Total donations in group:', donationsRes.data?.length || 0);

          // Filter pledges by member_id (also uses native_users.id)
          const userPledges = pledgesRes.data && nativeUserId
            ? (pledgesRes.data || []).filter(p => {
                const matches = p.member_id === nativeUserId;
                console.log('🔍 Checking pledge:', p.id, 'member_id:', p.member_id, 'nativeUserId:', nativeUserId, 'matches?', matches);
                if (matches) {
                  console.log('✅ Found matching pledge:', p.id);
                }
                return matches;
              })
            : [];

          console.log('✅ User pledges found:', userPledges.length);
          console.log('📊 Total pledges in group:', pledgesRes.data?.length || 0);

          // Fetch RSVPs and ticket purchases for all events IN PARALLEL
          const allRsvps = [];
          const allTickets = [];
          
          if (eventsRes.success && eventsRes.data) {
            console.log('📅 Fetching RSVPs and tickets for', eventsRes.data.length, 'events...');
            
            // Fetch all RSVPs and tickets in parallel for better performance
            const eventDataPromises = eventsRes.data.map(async (event) => {
              try {
                const [rsvpsRes, ticketsRes] = await Promise.all([
                  groupService.getEventRsvps(adminGroup.id, event.id).catch(err => {
                    console.error(`Error fetching RSVPs for event ${event.id}:`, err);
                    return { success: false, data: [] };
                  }),
                  event.requires_payment 
                    ? groupService.getEventTicketPurchases(adminGroup.id, event.id).catch(err => {
                        console.error(`Error fetching tickets for event ${event.id}:`, err);
                        return { success: false, data: [] };
                      })
                    : Promise.resolve({ success: false, data: [] })
                ]);

                const userRsvps = rsvpsRes.success && rsvpsRes.data
                  ? rsvpsRes.data.filter(r => r.user_id === userId).map(rsvp => ({ ...rsvp, events: event }))
                  : [];
                
                const userTickets = ticketsRes.success && ticketsRes.data
                  ? ticketsRes.data.filter(t => t.user_id === userId).map(ticket => ({ ...ticket, events: event }))
                  : [];

                return { rsvps: userRsvps, tickets: userTickets };
              } catch (err) {
                console.error(`Error fetching data for event ${event.id}:`, err);
                return { rsvps: [], tickets: [] };
              }
            });

            // Wait for all event data to be fetched
            const eventDataResults = await Promise.all(eventDataPromises);
            
            // Combine all results
            eventDataResults.forEach(result => {
              allRsvps.push(...result.rsvps);
              allTickets.push(...result.tickets);
            });
            
            console.log('✅ Found', allRsvps.length, 'RSVPs and', allTickets.length, 'tickets');
          }

          // Calculate summary
          const totalDonations = userDonations.reduce((sum, d) => 
            sum + (d.amount_cents || 0) / 100, 0
          );
          const totalTickets = allTickets.reduce((sum, t) => 
            sum + (t.total_amount_cents || 0) / 100, 0
          );
          const totalPledges = userPledges.reduce((sum, p) => 
            sum + (p.amount || 0), 0
          );
          const unpaidPledges = userPledges
            .filter(p => p.payment_status !== 'paid')
            .reduce((sum, p) => sum + (p.amount || 0), 0);

          // Update member data with fetched details
          setMemberData(prev => ({
            ...prev,
            rsvps: allRsvps,
            ticketPurchases: allTickets,
            donations: userDonations,
            pledges: userPledges,
            summary: {
              totalDonations,
              totalTicketPurchases: totalTickets,
              totalPledges,
              unpaidPledges
            }
          }));
          
        } catch (err) {
          console.error('Error fetching additional member data:', err);
          setError('Some data could not be loaded');
        } finally {
          setLoading(false);
        }
        return;
      }
      
      // If no passed data, try to fetch full profile (fallback)
      setLoading(true);
      setError('Member data not found. Please return to members list.');
      setLoading(false);
    };

    fetchAdditionalData();
  }, [adminGroup, userId, passedMemberData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-gray-500">Loading member profile...</div>
        </div>
      </AdminLayout>
    );
  }

  const { profile, rsvps, ticketPurchases, donations, pledges, summary } = memberData || {
    profile: {},
    rsvps: [],
    ticketPurchases: [],
    donations: [],
    pledges: [],
    summary: {
      totalDonations: 0,
      totalTicketPurchases: 0,
      totalPledges: 0,
      unpaidPledges: 0
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
            <p className="mt-2 text-sm">The data below may be incomplete or unavailable.</p>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/admin/dashboard/members`)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Members
          </button>
          
          <div className="flex items-center space-x-4">
            {profile.avatar_url ? (
              <img 
                src={profile.avatar_url} 
                alt={profile.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-semibold">
                {profile.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {profile.name || (error ? 'Member Profile' : 'Unknown Member')}
              </h1>
              <p className="text-gray-600">
                {profile.created_at ? `App member since ${formatDate(profile.created_at)}` : 'Loading member information...'}
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {loading ? (
            <>
              <SummaryCardSkeleton />
              <SummaryCardSkeleton />
              <SummaryCardSkeleton />
              <SummaryCardSkeleton />
            </>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Donations</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(summary.totalDonations)}
                    </p>
                  </div>
                  <FontAwesomeIcon icon={faDollarSign} className="text-3xl text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Ticket Purchases</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(summary.totalTicketPurchases)}
                    </p>
                  </div>
                  <FontAwesomeIcon icon={faTicketAlt} className="text-3xl text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Pledges</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(summary.totalPledges)}
                    </p>
                  </div>
                  <FontAwesomeIcon icon={faHandHoldingHeart} className="text-3xl text-purple-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Unpaid Pledges</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatCurrency(summary.unpaidPledges)}
                    </p>
                  </div>
                  <FontAwesomeIcon icon={faHourglassHalf} className="text-3xl text-orange-500" />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => toggleSection('profile')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
            <FontAwesomeIcon 
              icon={expandedSections.profile ? faChevronUp : faChevronDown} 
              className="text-gray-500"
            />
          </button>
          
          {expandedSections.profile && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {loading ? (
                <div className="py-4">
                  <LoadingSkeleton rows={4} />
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-gray-900">{profile.email || '—'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-gray-900">{profile.phone || '—'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-gray-900">
                      {[profile.address, profile.city, profile.state, profile.zip_code]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faUser} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Family Status</p>
                    <p className="text-gray-900">{profile.family_status || '—'}</p>
                  </div>
                </div>

                {profile.anniversary && (
                  <div className="flex items-start space-x-3">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Anniversary</p>
                      <p className="text-gray-900">{formatDate(profile.anniversary)}</p>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          )}
        </div>

        {/* Donations */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => toggleSection('donations')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">
              Donations ({donations.length})
            </h2>
            <FontAwesomeIcon 
              icon={expandedSections.donations ? faChevronUp : faChevronDown} 
              className="text-gray-500"
            />
          </button>
          
          {expandedSections.donations && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {loading ? (
                <div className="py-4">
                  <LoadingSkeleton rows={5} />
                </div>
              ) : donations.length === 0 ? (
                <p className="text-gray-500 py-4">No donations</p>
              ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dedication</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {donations.map((donation) => (
                        <tr key={donation.id}>
                          <td className="px-4 py-4 text-sm text-gray-900 font-semibold">
                            {formatCurrency(donation.amount_cents / 100, donation.currency)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">{donation.donation_type || '—'}</td>
                          <td className="px-4 py-4 text-sm">
                            {donation.product_type ? (
                              <span 
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${donation.product_type.color}20`,
                                  color: donation.product_type.color 
                                }}
                              >
                                {donation.product_type.name}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">{donation.dedication || '—'}</td>
                          <td className="px-4 py-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              donation.status === 'succeeded' 
                                ? 'bg-green-100 text-green-800' 
                                : donation.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {donation.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {formatDate(donation.succeeded_at || donation.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pledges */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => toggleSection('pledges')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">
              Pledges ({pledges.length})
            </h2>
            <FontAwesomeIcon 
              icon={expandedSections.pledges ? faChevronUp : faChevronDown} 
              className="text-gray-500"
            />
          </button>
          
          {expandedSections.pledges && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {loading ? (
                <div className="py-4">
                  <LoadingSkeleton rows={5} />
                </div>
              ) : pledges.length === 0 ? (
                <p className="text-gray-500 py-4">No pledges</p>
              ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pledges.map((pledge) => (
                        <tr key={pledge.id}>
                          <td className="px-4 py-4 text-sm text-gray-900 font-semibold">
                            {formatCurrency(pledge.amount, pledge.currency)}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {pledge.pledge_type ? (
                              <span 
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${pledge.pledge_type.color}20`,
                                  color: pledge.pledge_type.color 
                                }}
                              >
                                {pledge.pledge_type.name}
                              </span>
                            ) : (
                              <span className="text-gray-500">General</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">{pledge.description || '—'}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{formatDate(pledge.due_date)}</td>
                          <td className="px-4 py-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              pledge.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-800' 
                                : pledge.payment_status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              <FontAwesomeIcon 
                                icon={pledge.payment_status === 'paid' ? faCheckCircle : faHourglassHalf} 
                                className="mr-1"
                              />
                              {pledge.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">{formatDate(pledge.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Event RSVPs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => toggleSection('rsvps')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">
              Event Registrations ({rsvps.length})
            </h2>
            <FontAwesomeIcon 
              icon={expandedSections.rsvps ? faChevronUp : faChevronDown} 
              className="text-gray-500"
            />
          </button>
          
          {expandedSections.rsvps && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {loading ? (
                <div className="py-4">
                  <LoadingSkeleton rows={4} />
                </div>
              ) : rsvps.length === 0 ? (
                <p className="text-gray-500 py-4">No event registrations</p>
              ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rsvps.map((rsvp) => (
                        <tr key={rsvp.id}>
                          <td className="px-4 py-4 text-sm text-gray-900">{rsvp.events?.title || '—'}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{formatDateTime(rsvp.events?.start_time)}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{rsvp.events?.location || '—'}</td>
                          <td className="px-4 py-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              rsvp.status === 'going' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {rsvp.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">{formatDate(rsvp.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Ticket Purchases */}
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => toggleSection('tickets')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900">
              Ticket Purchases ({ticketPurchases.length})
            </h2>
            <FontAwesomeIcon 
              icon={expandedSections.tickets ? faChevronUp : faChevronDown} 
              className="text-gray-500"
            />
          </button>
          
          {expandedSections.tickets && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {loading ? (
                <div className="py-4">
                  <LoadingSkeleton rows={4} />
                </div>
              ) : ticketPurchases.length === 0 ? (
                <p className="text-gray-500 py-4">No ticket purchases</p>
              ) : (
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmation #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tickets</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ticketPurchases.map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="px-4 py-4 text-sm text-gray-900">{ticket.events?.title || '—'}</td>
                          <td className="px-4 py-4 text-sm text-gray-600 font-mono">{ticket.confirmation_number || '—'}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{ticket.attendee_count}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 font-semibold">
                            {formatCurrency(ticket.total_amount_cents / 100)}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              ticket.payment_status === 'succeeded' 
                                ? 'bg-green-100 text-green-800' 
                                : ticket.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {ticket.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {formatDate(ticket.payment_completed_at || ticket.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default MemberProfilePage;
